import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import Chip from './Chip';
import FormModal from '@shared/ui/FormModal';
import WorkspacePickerList from './workspace/WorkspacePickerList';

interface Workspace {
  id: string;
  name: string;
  description: string;
  owner_org_name: string;
}

interface LegislatorWorkspaceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  legislatorId: string;
  legislatorName: string;
  onUpdate?: () => void;
}

const LegislatorWorkspaceSelectionModal: React.FC<LegislatorWorkspaceSelectionModalProps> = ({
  isOpen,
  onClose,
  legislatorId,
  legislatorName,
  onUpdate
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [currentTrackingWorkspaces, setCurrentTrackingWorkspaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch eligible workspaces when modal opens
  useEffect(() => {
    if (isOpen && legislatorId) {
      fetchWorkspaces();
      fetchCurrentTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, legislatorId]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workspaces');
      setWorkspaces(response.data || []);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentTracking = async () => {
    try {
      const response = await api.get(`/workspace-legislator-tracking/legislator/${legislatorId}/workspaces`);
      if (response.data.success) {
        const trackingWorkspaceIds = response.data.data.map((tracking: any) => tracking.workspace_id);
        setCurrentTrackingWorkspaces(trackingWorkspaceIds);
        setSelectedWorkspaces(trackingWorkspaceIds);
      }
    } catch (error) {
      console.error('Error fetching current tracking:', error);
    }
  };

  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaces(prev => 
      prev.includes(workspaceId) 
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Determine which workspaces to add and remove
      const workspacesToAdd = selectedWorkspaces.filter(id => !currentTrackingWorkspaces.includes(id));
      const workspacesToRemove = currentTrackingWorkspaces.filter(id => !selectedWorkspaces.includes(id));
      
      // Add to new workspaces
      if (workspacesToAdd.length > 0) {
        await api.post('/workspace-legislator-tracking', {
          legislatorId: parseInt(legislatorId),
          workspaceIds: workspacesToAdd,
          // Don't pass position to let backend use workspace default
          isPriority: false,
          notes: ''
        });
      }
      
      // Remove from workspaces
      if (workspacesToRemove.length > 0) {
        await api.delete('/workspace-legislator-tracking', {
          data: {
            legislatorId: parseInt(legislatorId),
            workspaceIds: workspacesToRemove
          }
        });
      }
      
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating legislator tracking:', error);
      alert('Failed to update legislator tracking. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedWorkspaces(currentTrackingWorkspaces);
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      title="Track legislator"
      description={legislatorName}
      onClose={handleClose}
      onSubmit={handleSave}
      size="lg"
      submitLabel="Save changes"
      isSubmitting={saving}
      footerLeft={
        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {selectedWorkspaces.length} workspace{selectedWorkspaces.length !== 1 ? 's' : ''} selected
        </span>
      }
    >
      <WorkspacePickerList
        workspaces={workspaces}
        selectedIds={selectedWorkspaces}
        onToggle={handleWorkspaceToggle}
        loading={loading}
        prompt="Select workspaces where you want to track this legislator:"
        renderMeta={(workspace) =>
          currentTrackingWorkspaces.includes(workspace.id) ? (
            <Chip label="Currently tracked" tone="positive" size="sm" />
          ) : null
        }
      />
    </FormModal>
  );
};

export default LegislatorWorkspaceSelectionModal;
